import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCurrentOrganizationId } from '@repo/shared-kit/organizations';
import { navigationQueries } from '@repo/api-kit/queries/navigation';
import {
  type FourthlineProcessResponse,
  type FourthlineProviderDetails,
  kycMutations,
} from '@repo/api-kit/queries/due-diligence/kyc';

interface FourthlineProcess {
  token: string;
  redirectUrl: string | undefined;
  legacyProcess: FourthlineProcessResponse | undefined;
  isLoading: boolean;
}

/**
 * Resolves Fourthline SDK process (token + redirectUrl) from either
 * the acceptance API or the legacy PUT /fourthline_process endpoint.
 *
 * @deprecated Legacy fallback path — remove when BE adds `providerDetails` to the acceptance API.
 */
export function useFourthlineProcess(
  providerDetails: FourthlineProviderDetails | null
): FourthlineProcess {
  const organizationId = useCurrentOrganizationId();

  const { data: membership, isLoading: isMembershipLoading } = useQuery({
    ...navigationQueries.getMembershipsMeAggregate(organizationId),
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    enabled: !providerDetails,
  });

  const identityId = membership?.identityId ?? '';

  const {
    mutate,
    data: legacyProcess,
    isPending: isMutationPending,
    isIdle: isMutationIdle,
  } = useMutation(kycMutations.createFourthlineProcess(identityId));

  const isLegacyProviderDetailsLoading = (!providerDetails && isMutationIdle) || isMutationPending;

  useEffect(() => {
    if (identityId && isMutationIdle) {
      mutate();
    }
  }, [identityId, isMutationIdle, mutate]);

  return {
    token: providerDetails?.token ?? legacyProcess?.providerToken ?? '',
    redirectUrl: providerDetails?.redirectUrl ?? legacyProcess?.redirectUrl ?? '',
    legacyProcess: providerDetails ? undefined : legacyProcess,
    isLoading: isMembershipLoading || isLegacyProviderDetailsLoading,
  };
}
